import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const DEFAULT_FAVORITES = ['/dashboard', '/venues', '/live-orders'];
const DEFAULT_NAV_ORDER = [
  'nav.dashboard',
  'nav.distillery',
  'insights.brewmaster',
  'nav.gastronomy',
  'nav.trends',
  'nav.settings',
];

interface CustomFavorite {
  label: string;
  iconName: string;
}

interface NavigationState {
  favorites: string[];
  customFavorites: Record<string, CustomFavorite>;
  navOrder: string[];
  editMode: boolean;
  toggleFavorite: (href: string) => void;
  addCustomFavorite: (href: string, label: string, iconName: string) => void;
  removeCustomFavorite: (href: string) => void;
  setNavOrder: (order: string[]) => void;
  setEditMode: (on: boolean) => void;
  resetToDefaults: () => void;
}

export const useNavigationStore = create<NavigationState>()(
  persist(
    (set, get) => ({
      favorites: DEFAULT_FAVORITES,
      customFavorites: {},
      navOrder: DEFAULT_NAV_ORDER,
      editMode: false,

      toggleFavorite: (href: string) => {
        const { favorites, customFavorites } = get();
        if (favorites.includes(href)) {
          const { [href]: _, ...rest } = customFavorites;
          set({ favorites: favorites.filter((f) => f !== href), customFavorites: rest });
        } else {
          set({ favorites: [...favorites, href] });
        }
      },

      addCustomFavorite: (href: string, label: string, iconName: string) => {
        const { favorites, customFavorites } = get();
        set({
          favorites: favorites.includes(href) ? favorites : [...favorites, href],
          customFavorites: { ...customFavorites, [href]: { label, iconName } },
        });
      },

      removeCustomFavorite: (href: string) => {
        const { favorites, customFavorites } = get();
        const { [href]: _, ...rest } = customFavorites;
        set({
          favorites: favorites.filter((f) => f !== href),
          customFavorites: rest,
        });
      },

      setNavOrder: (order: string[]) => {
        set({ navOrder: order });
      },

      setEditMode: (on: boolean) => {
        set({ editMode: on });
      },

      resetToDefaults: () => {
        set({
          favorites: DEFAULT_FAVORITES,
          customFavorites: {},
          navOrder: DEFAULT_NAV_ORDER,
          editMode: false,
        });
      },
    }),
    {
      name: 'b2b-nav-prefs',
      partialize: (state) => ({
        favorites: state.favorites,
        customFavorites: state.customFavorites,
        navOrder: state.navOrder,
      }),
    }
  )
);
